/**
 * Read-only presentation of structured note sections.
 *
 * Used inside {@link StructuredNoteField} display mode (with optional "Edit note")
 * and by {@link StructuredNoteContent} on summary / detail pages (no edit button).
 */

import { Button, Space, Typography, theme } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { StructuredNoteSections } from './types';
import { SimpleList } from '@/components/ui/SimpleList';

const { Text, Title } = Typography;

interface StructuredNoteDisplayProps {
  structuredNote: StructuredNoteSections;
  /** When true, shows an "Edit note" button that calls `onEdit`. */
  showEditButton?: boolean;
  onEdit?: () => void;
}

/** Renders a titled list of non-empty text bullets; hides empty sections. */
function TextSection({
  title,
  items,
}: {
  title: string;
  items: Array<{ text: string }>;
}) {
  const { token } = theme.useToken();
  const visibleItems = items.map((item) => item.text.trim()).filter(Boolean);
  if (visibleItems.length === 0) return null;

  return (
    <div>
      <Title level={5} style={{ marginTop: 0, marginBottom: token.marginXS }}>
        {title}
      </Title>
      <SimpleList
        items={visibleItems.map((text, index) => ({ key: index, text }))}
        rowKey="key"
        split={false}
        flush
        renderItem={(item) => <Text>{item.text}</Text>}
      />
    </div>
  );
}

/** Renders next steps with optional responsible / deadline metadata. */
function NextStepsSection({
  title,
  items,
  responsibleLabel,
  deadlineLabel,
}: {
  title: string;
  items: StructuredNoteSections['nextSteps'];
  responsibleLabel: string;
  deadlineLabel: string;
}) {
  const { token } = theme.useToken();
  const visibleItems = items.filter((item) => item.task.trim());
  if (visibleItems.length === 0) return null;

  return (
    <div>
      <Title level={5} style={{ marginTop: 0, marginBottom: token.marginXS }}>
        {title}
      </Title>
      <SimpleList
        items={visibleItems.map((item, index) => ({ ...item, key: index }))}
        rowKey="key"
        split={false}
        flush
        renderItem={(item) => (
            <Space orientation="vertical" size={0}>
              <Text>{item.task}</Text>
              {item.responsible?.trim() ? (
                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  {responsibleLabel}: {item.responsible}
                </Text>
              ) : null}
              {item.deadline?.trim() ? (
                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  {deadlineLabel}: {item.deadline}
                </Text>
              ) : null}
            </Space>
        )}
      />
    </div>
  );
}

/**
 * Formatted structured note view for takeaways, agreements, and next steps.
 */
export function StructuredNoteDisplay({
  structuredNote,
  showEditButton = false,
  onEdit,
}: StructuredNoteDisplayProps) {
  const { token } = theme.useToken();
  const { t } = useTranslation('common');

  const hasContent =
    structuredNote.keyTakeaways.some((item) => item.text.trim()) ||
    structuredNote.agreementsDecisions.some((item) => item.text.trim()) ||
    structuredNote.nextSteps.some((item) => item.task.trim());

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      {!hasContent ? (
        <Text type="secondary">{t('structuredNote.emptyStructured')}</Text>
      ) : (
        <>
          <TextSection
            title={t('structuredNote.sections.keyTakeaways')}
            items={structuredNote.keyTakeaways}
          />
          <TextSection
            title={t('structuredNote.sections.agreementsDecisions')}
            items={structuredNote.agreementsDecisions}
          />
          <NextStepsSection
            title={t('structuredNote.sections.nextSteps')}
            items={structuredNote.nextSteps}
            responsibleLabel={t('structuredNote.labels.responsible')}
            deadlineLabel={t('structuredNote.labels.deadline')}
          />
        </>
      )}

      {showEditButton && onEdit ? (
        <Button icon={<EditOutlined />} onClick={onEdit} style={{ marginTop: token.marginXS }}>
          {t('structuredNote.editNote')}
        </Button>
      ) : null}
    </Space>
  );
}
